/**
 * Toast notifications. Body-level, imperative - toasts can
 * fire from any module (e.g. export success, "select a ship first"), so this is
 * a plain service rather than a Lit component. Markup/animation match the blob;
 * message text goes through `textContent` (no manual HTML escaping needed).
 */

export type ToastType = 'success' | 'error';

let container: HTMLElement | null = null;

function ensureContainer(): HTMLElement {
  if (container && container.isConnected) return container;
  container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'toast-container';
    container.id = 'toast-container';
    document.body.appendChild(container);
  }
  return container;
}

export function dismissToast(toast: HTMLElement | null): void {
  if (!toast || toast.classList.contains('fade-out')) return;
  toast.classList.add('fade-out');
  toast.addEventListener('animationend', () => toast.remove());
}

/**
 * `duration` 0 keeps the toast until it is closed - for an error that says
 * why something did not happen, which nobody should have to catch in 4 s.
 * A trailing "(Reference: …)" from `userMessage` goes on a line of its own.
 */
export function showToast(
  type: ToastType,
  message: string,
  duration = 4000,
): HTMLElement {
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  if (type === 'error') toast.setAttribute('role', 'alert');

  const icon = document.createElement('span');
  icon.className = 'toast-icon';
  icon.textContent = type === 'success' ? '✓' : '✗';

  const msg = document.createElement('span');
  msg.className = 'toast-msg';
  const ref = /\s*\(Reference: ([^)]+)\)$/.exec(message);
  msg.textContent = ref ? message.slice(0, ref.index) : message;
  if (ref) {
    const line = document.createElement('span');
    line.className = 'toast-ref';
    line.textContent = `Reference ${ref[1]}`;
    msg.append(line);
  }

  const close = document.createElement('button');
  close.className = 'toast-close';
  close.textContent = '×';
  close.addEventListener('click', () => dismissToast(toast));

  toast.append(icon, msg, close);
  ensureContainer().appendChild(toast);
  if (duration > 0) setTimeout(() => dismissToast(toast), duration);
  return toast;
}
